import { layoutSelector } from 'state';
import { useRecoilState } from 'recoil';
import { Box } from '@mui/system';
import { LayoutItem } from 'api/models';
import FieldTemplateRenderer from '../../FieldTemplateRenderer';
import { produce } from 'immer';
import { useState } from 'react';
import { get, set } from 'object-path';
import { DropArea } from '../../DropArea';
import ColumnControls from './ColumnControls';
import { ResizableBox } from 'react-resizable';
import { getWidthFromXs, getXsFromWidth } from 'routes/Form/utils';
import { VerticalAlignCenter } from '@mui/icons-material';

interface LayoutFieldColumnProps {
  columnIndex: number;
  rowWidth: number;
  path: string;
  editDisabled?: boolean;
}

export default function LayoutFieldColumn({
  columnIndex,
  path,
  editDisabled,
  rowWidth,
}: LayoutFieldColumnProps) {
  const columnsPath = `${path}.children`;
  const fieldsPath = `${path}.children.${columnIndex}.children`;
  const layoutPath = `${path}.children.${columnIndex}.layoutOptions.layout`;
  const [layout, setLayout] = useRecoilState(layoutSelector);
  // not the nicest thing but we need to force a rerender of the resizable box if there was no change
  const [renderId, setRenderId] = useState('');

  const onResizeEnd = (resizeWidth: number) => {
    const xs = getXsFromWidth({
      resizeWidth,
      rowWidth,
    });
    const columns = get(layout, columnsPath);
    const sum = columns.reduce(
      (acc: number, column: LayoutItem, index: number) => {
        if (index === columnIndex) {
          return acc + parseInt(xs ?? '');
        }
        return acc + parseInt(column?.layoutOptions?.layout ?? '12');
      },
      0,
    );

    if (sum >= 12) {
      const remainingSpace = 12 - sum + parseInt(xs ?? '');
      const items = produce(layout, draft => {
        if (get(draft, layoutPath) !== undefined) {
          set(draft, layoutPath, remainingSpace.toString());
        }
      });
      setLayout(items);
    } else {
      const items = produce(layout, draft => {
        if (get(draft, layoutPath) !== undefined) {
          set(draft, layoutPath, xs);
        }
      });
      setLayout(items);
    }
    // need to force a rerender because of the ResizableBox
    // it can happen that the XS is not changing but the width is
    setRenderId(`${Date.now()}`);
  };

  return (
    <ResizableBox
      key={renderId}
      onResizeStop={(e, { size }) => onResizeEnd(size.width)}
      axis="x"
      width={getWidthFromXs(get(layout, layoutPath), rowWidth)}
      resizeHandles={editDisabled ? [] : ['e']}
      handle={
        <Box
          width="100%"
          display="flex"
          justifyContent={'end'}
          height="20px"
          mt={'-30px'}
          pr={'15px'}
        >
          <VerticalAlignCenter
            fontSize="small"
            sx={{
              transform: 'rotate(90deg)',
              cursor: 'col-resize',
              color: theme =>
                editDisabled
                  ? theme.palette.text.disabled
                  : theme.palette.primary.main,
            }}
          />
        </Box>
      }
    >
      <Box
        sx={{
          minHeight: '165px',
          p: 1,
          pt: 0,
        }}
      >
        <Box
          sx={{
            backgroundColor: theme => theme.palette.action.hover,
            boxShadow: theme => `0px 0px 2px 1px ${theme.palette.divider}`,
            borderRadius: '5px',
            p: 1,
            minHeight: '165px',
          }}
        >
          <ColumnControls
            path={path}
            columnIndex={columnIndex}
            editDisabled={editDisabled}
            width={parseInt(get(layout, layoutPath)) ?? 12}
          />
          {get(layout, fieldsPath).length !== 0 && (
            <DropArea parentPath={fieldsPath} index={0} />
          )}

          {get(layout, fieldsPath).map(
            (layoutItem: LayoutItem, fieldIndex: number) => {
              return (
                <FieldTemplateRenderer
                  key={`layout-field-${layoutItem.id}`}
                  index={fieldIndex}
                  parentPath={`${path}.children.${columnIndex}.children`}
                  layoutItem={layoutItem}
                  displayAddButton={false}
                  disabled={editDisabled}
                />
              );
            },
          )}

          {get(layout, fieldsPath).length === 0 && (
            <DropArea parentPath={fieldsPath} index={0} />
          )}
        </Box>
      </Box>
    </ResizableBox>
  );
}
